import { useForm } from 'react-hook-form';
import { cn } from '@/lib/utils.ts';
import {
  ColorCategories,
  ColoringBands,
  ColoringFlat,
  ColoringNone,
  ColoringType,
  ColorScale,
  ContentFormat,
  createDataLayer,
  DataLayer,
  DataLayerData,
  DataLayerDataSchema,
  DataLayerIcon,
  DataLayerParameter,
  DataLayerPlain,
  DataLayerPlainSchema,
  DataLayerStory,
  DataLayerStorySchema,
  DataLayerType,
  FlatLegendType,
  MarkerLayer,
  ParameterCoordinate,
  ParameterCoupled,
  ParameterDependentChoice,
  ParameterSelect,
  ParameterSelectDisplayType,
  ParameterSlider,
  ParameterType,
  Source,
  SourceGeoJson,
  SourceGeoRaster,
  SourceTilesTMS,
  SourceType,
  updateDataLayer,
} from '@/api/api.ts';
import ColoringNoneForm from './coloring/ColoringNoneForm';
import ColoringFlatForm from './coloring/ColoringFlatForm';
import ColoringCategoriesForm from './coloring/ColoringCategoriesForm';
import ColoringBandsForm from './coloring/ColoringBandsForm';
import ColoringScaleForm from './coloring/ColoringScaleForm';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useState } from 'react';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import { TiptapEditor } from '@/components/ui/tiptap-editor.tsx';
import z from 'zod';

// Use full schemas for comprehensive DataLayer editing
const getSchemaForType = (type: DataLayerType) => {
  switch (type) {
    case DataLayerType.Story:
      return DataLayerStorySchema;
    case DataLayerType.Data:
      return DataLayerDataSchema;
    case DataLayerType.PlainProject:
      return DataLayerPlainSchema;
    default:
      return DataLayerStorySchema; // fallback
  }
};

export default function DataLayerForm({
  dataLayer,
  projectId,
  onClose,
  contentFormat = ContentFormat.Text,
  isEditing = true,
}: {
  dataLayer: (DataLayer | DataLayerPlain) & { id?: string };
  projectId: string;
  onClose: () => void;
  contentFormat?: ContentFormat;
  isEditing?: boolean;
}) {
  const [saving, setSaving] = useState(false);

  // Use the existing exported types from api.ts
  type FormData = DataLayerStory | DataLayerData | DataLayerPlain;

  // Create a discriminated union schema that matches FormData
  const discriminatedSchema = z.discriminatedUnion('type', [
    DataLayerStorySchema,
    DataLayerDataSchema,
    DataLayerPlainSchema,
  ]);

  // Create proper default values based on the dataLayer type
  const getDefaultValues = (): Partial<FormData> => {
    const base = {
      id: dataLayer.id || '',
      type: dataLayer.type,
    };

    switch (dataLayer.type) {
      case DataLayerType.Story:
        const storyLayer = dataLayer as DataLayerStory;
        return {
          ...base,
          shortName: storyLayer.shortName || '',
          shortNameDe: storyLayer.shortNameDe || '',
          defaultVisibility: storyLayer.defaultVisibility ?? true,
          icon: storyLayer.icon || DataLayerIcon.Story,
          layers: storyLayer.layers || [
            { content: '', contentDe: null, markers: [[0, 0]] },
          ],
        } as Partial<FormData>;

      case DataLayerType.Data:
        const dataLayer_ = dataLayer as DataLayerData;
        return {
          ...base,
          shortName: dataLayer_.shortName || '',
          shortNameDe: dataLayer_.shortNameDe || '',
          defaultVisibility: dataLayer_.defaultVisibility ?? true,
          icon: dataLayer_.icon || DataLayerIcon.Static,
          parameters: dataLayer_.parameters || {},
          dataSources: dataLayer_.dataSources || [],
          plots: dataLayer_.plots || null,
        } as Partial<FormData>;

      case DataLayerType.PlainProject:
        const plainLayer = dataLayer as DataLayerPlain;
        return {
          ...base,
          markers: plainLayer.markers || [[0, 0]],
        } as Partial<FormData>;

      default:
        return base as Partial<FormData>;
    }
  };

  // PoC: Use single form with discriminated union
  const form = useForm<FormData>({
    resolver: zodResolver(discriminatedSchema),
    defaultValues: getDefaultValues(),
  });

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        setSaving(true);

        // Ensure data has required id field
        if (!data.id) {
          throw new Error('DataLayer ID is required');
        }

        // Use createDataLayer for new layers, updateDataLayer for existing ones
        if (isEditing) {
          await updateDataLayer(projectId, data.id, data);
        } else {
          await createDataLayer(projectId, data);
        }

        onClose();
      } catch (error) {
        alert(
          `Speichern fehlgeschlagen, bitte erneut versuchen: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`
        );
      } finally {
        setSaving(false);
      }
    },
    [projectId, onClose, isEditing]
  );

  // Use the dataLayer type directly since it's already known
  const watchedType = dataLayer.type;

  return (
    <>
      <div className="mb-4">
        <h2 className="text-lg font-bold">
          {isEditing ? 'Edit' : 'Create'} Data Layer
        </h2>
        <p className="text-sm text-gray-600">
          Type: {watchedType} (
          {
            {
              [DataLayerType.Story]: 'Story Layer',
              [DataLayerType.Data]: 'Geodata Layer',
              [DataLayerType.PlainProject]: 'Marker Group Layer',
            }[watchedType]
          }
          )
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className={cn('py-[8px]')}>
            <FormField
              control={form.control}
              name="id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ID</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={'DataLayer ID'}
                      disabled={saving || isEditing}
                    />
                  </FormControl>
                  <FormDescription>
                    {isEditing
                      ? 'ID cannot be changed when editing'
                      : 'Unique identifier for the data layer'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className={cn('py-[8px]')}>
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      disabled={saving || isEditing}
                      className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {Object.values(DataLayerType).map((type) => (
                        <option key={type} value={type}>
                          {type === DataLayerType.Story && 'Story Layer'}
                          {type === DataLayerType.Data && 'Geodata Layer'}
                          {type === DataLayerType.PlainProject &&
                            'Marker Group Layer'}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormDescription>
                    {isEditing
                      ? 'Type cannot be changed when editing'
                      : 'Select the data layer type'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Show shortName fields for Story and Data layers only */}
          {(watchedType === DataLayerType.Story ||
            watchedType === DataLayerType.Data) && (
            <div className={cn('block', 'lg:flex', 'gap-x-[24px]')}>
              <div className={cn('flex-1', 'py-[8px]')}>
                <FormField
                  control={form.control}
                  name="shortName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Short Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={'Short Name'}
                          disabled={saving}
                          icon={<span>🇺🇸</span>}
                        />
                      </FormControl>
                      <FormDescription />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className={cn('flex-1', 'py-[8px]')}>
                <FormField
                  control={form.control}
                  name="shortNameDe"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Short Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          placeholder={'Short Name'}
                          disabled={saving}
                          icon={<span>🇩🇪</span>}
                        />
                      </FormControl>
                      <FormDescription />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          )}

          {/* Default Visibility for Story and Data layers */}
          {(watchedType === DataLayerType.Story ||
            watchedType === DataLayerType.Data) && (
            <div className={cn('py-[8px]')}>
              <FormField
                control={form.control}
                name="defaultVisibility"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={saving}
                        className="h-4 w-4"
                      />
                    </FormControl>
                    <div>
                      <FormLabel>Default Visibility</FormLabel>
                      <FormDescription>
                        Whether this layer is visible by default
                      </FormDescription>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {/* Story Layer specific content fields */}
          {watchedType === DataLayerType.Story && (
            <div className="mt-6 p-4 border rounded-md bg-blue-50">
              <h3 className="font-semibold mb-4">Story Points</h3>

              <FormField
                control={form.control}
                name="layers"
                render={({ field }) => {
                  const layers = (field.value as MarkerLayer[]) || [];

                  const addStoryPoint = () => {
                    const newLayers = [
                      ...layers,
                      { content: '', contentDe: null, markers: [[0, 0]] },
                    ];
                    field.onChange(newLayers);
                  };

                  const removeStoryPoint = (index: number) => {
                    if (
                      confirm(
                        'Are you sure you want to remove this story point?\n\nAll story content and markers will be lost instantly and cannot be recovered.'
                      )
                    ) {
                      const newLayers = layers.filter((_, i) => i !== index);
                      field.onChange(newLayers);
                    }
                  };

                  const updateStoryPoint = (
                    index: number,
                    updates: Partial<MarkerLayer>
                  ) => {
                    const newLayers = [...layers];
                    newLayers[index] = { ...newLayers[index], ...updates };
                    field.onChange(newLayers);
                  };

                  const addMarker = (storyIndex: number) => {
                    const newLayers = [...layers];
                    newLayers[storyIndex].markers.push([0, 0]);
                    field.onChange(newLayers);
                  };

                  const removeMarker = (
                    storyIndex: number,
                    markerIndex: number
                  ) => {
                    if (
                      confirm(
                        'Are you sure you want to remove this marker?\n\nThe marker coordinates will be lost instantly and cannot be recovered.'
                      )
                    ) {
                      const newLayers = [...layers];
                      newLayers[storyIndex].markers = newLayers[
                        storyIndex
                      ].markers.filter((_, i) => i !== markerIndex);
                      field.onChange(newLayers);
                    }
                  };

                  const updateMarker = (
                    storyIndex: number,
                    markerIndex: number,
                    lat: number,
                    lng: number
                  ) => {
                    const newLayers = [...layers];
                    newLayers[storyIndex].markers[markerIndex] = [lat, lng];
                    field.onChange(newLayers);
                  };

                  return (
                    <FormItem>
                      <div className="space-y-4">
                        {layers.map((layer, storyIndex) => (
                          <div
                            key={storyIndex}
                            className="p-3 border rounded bg-white"
                          >
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="font-medium">
                                Story Point {storyIndex + 1}
                              </h4>
                              <button
                                type="button"
                                onClick={() => removeStoryPoint(storyIndex)}
                                disabled={saving || layers.length <= 1}
                                className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                              >
                                Remove
                              </button>
                            </div>

                            {/* Story Content */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="text-sm font-medium mb-2 block">
                                  Content 🇺🇸
                                </label>
                                {contentFormat === ContentFormat.Text && (
                                  <div className="relative">
                                    <textarea
                                      value={layer.content}
                                      onChange={(e) =>
                                        updateStoryPoint(storyIndex, {
                                          content: e.target.value,
                                        })
                                      }
                                      placeholder={''}
                                      disabled={saving}
                                      className={cn(
                                        'flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                                      )}
                                    />
                                  </div>
                                )}
                                {contentFormat === ContentFormat.Html && (
                                  <TiptapEditor
                                    value={layer.content || ''}
                                    onChange={(content) =>
                                      updateStoryPoint(storyIndex, {
                                        content: content,
                                      })
                                    }
                                    placeholder="Story content (English)"
                                    disabled={saving}
                                  />
                                )}
                              </div>
                              <div>
                                <label className="text-sm font-medium mb-2 block">
                                  Content 🇩🇪
                                </label>

                                {contentFormat === ContentFormat.Text && (
                                  <div className="relative">
                                    <textarea
                                      value={layer.contentDe || ''}
                                      onChange={(e) =>
                                        updateStoryPoint(storyIndex, {
                                          contentDe: e.target.value ?? null,
                                        })
                                      }
                                      placeholder={''}
                                      disabled={saving}
                                      className={cn(
                                        'flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                                      )}
                                    />
                                  </div>
                                )}
                                {contentFormat === ContentFormat.Html && (
                                  <TiptapEditor
                                    value={layer.contentDe || ''}
                                    onChange={(content) =>
                                      updateStoryPoint(storyIndex, {
                                        contentDe: content,
                                      })
                                    }
                                    placeholder="Story content (English)"
                                    disabled={saving}
                                  />
                                )}
                              </div>
                            </div>

                            {/* Markers */}
                            <div>
                              <label className="text-sm font-medium mb-2 block">
                                Markers
                              </label>
                              <div className="space-y-2">
                                {layer.markers.map((marker, markerIndex) => (
                                  <div
                                    key={markerIndex}
                                    className="flex gap-2 items-center"
                                  >
                                    <input
                                      type="number"
                                      value={marker[0]}
                                      onChange={(e) =>
                                        updateMarker(
                                          storyIndex,
                                          markerIndex,
                                          parseFloat(e.target.value) || 0,
                                          marker[1]
                                        )
                                      }
                                      placeholder="Latitude"
                                      disabled={saving}
                                      step="any"
                                      className="flex-1 px-2 py-1 text-sm border rounded"
                                    />
                                    <input
                                      type="number"
                                      value={marker[1]}
                                      onChange={(e) =>
                                        updateMarker(
                                          storyIndex,
                                          markerIndex,
                                          marker[0],
                                          parseFloat(e.target.value) || 0
                                        )
                                      }
                                      placeholder="Longitude"
                                      disabled={saving}
                                      step="any"
                                      className="flex-1 px-2 py-1 text-sm border rounded"
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeMarker(storyIndex, markerIndex)
                                      }
                                      disabled={
                                        saving || layer.markers.length <= 1
                                      }
                                      className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => addMarker(storyIndex)}
                                  disabled={saving}
                                  className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                                >
                                  + Add Marker
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={addStoryPoint}
                          disabled={saving}
                          className="px-4 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                        >
                          + Add Story Point
                        </button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>
          )}

          {/* Data Layer specific fields */}
          {watchedType === DataLayerType.Data && 'dataSources' in dataLayer && (
            <div className="mt-6 p-4 border rounded-md bg-green-50">
              <h3 className="font-semibold mb-4">
                Geodata Layer Configuration
              </h3>

              {/* Icon Selection */}
              <div className={cn('py-[8px]')}>
                <FormField
                  control={form.control}
                  name="icon"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Icon Type</FormLabel>
                      <FormControl>
                        <select
                          {...field}
                          disabled={saving}
                          className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <option value="static">Static</option>
                          <option value="model">Model</option>
                          <option value="series">Series</option>
                        </select>
                      </FormControl>
                      <FormDescription>
                        Select the icon type for this data layer
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Parameters Editor */}
              <div className="mt-6">
                <h4 className="font-medium text-sm mb-3">Parameters</h4>

                <FormField
                  control={form.control}
                  name="parameters"
                  render={({ field }) => {
                    const parameters =
                      (field.value as DataLayerData['parameters']) || {};
                    const parameterKeys = Object.keys(parameters);

                    const addParameter = (
                      parameterName: string,
                      parameterType: ParameterType
                    ) => {
                      if (!parameterName.trim() || parameters[parameterName])
                        return;

                      let newParameter: DataLayerParameter;

                      switch (parameterType) {
                        case ParameterType.Slider:
                          newParameter = {
                            type: ParameterType.Slider,
                            label: parameterName,
                            min: 0,
                            max: 100,
                            step: 1,
                            initValue: 50,
                          };
                          break;
                        case ParameterType.Select:
                          newParameter = {
                            type: ParameterType.Select,
                            label: parameterName,
                            options: [
                              { value: 'option1', label: 'Option 1' },
                              { value: 'option2', label: 'Option 2' },
                            ],
                            displayAs: ParameterSelectDisplayType.Dropdown,
                            initValue: 'option1',
                          };
                          break;
                        case ParameterType.DependentChoice:
                          newParameter = {
                            type: ParameterType.DependentChoice,
                            label: parameterName,
                            subLabel: 'Sub Option',
                            options: [
                              {
                                value: 'main1',
                                label: 'Main Option 1',
                                subOptions: [
                                  { value: 'sub1', label: 'Sub Option 1' },
                                  { value: 'sub2', label: 'Sub Option 2' },
                                ],
                              },
                            ],
                            initValue: ['main1', 'sub1'],
                          };
                          break;
                        case ParameterType.Coordinate:
                          newParameter = {
                            type: ParameterType.Coordinate,
                            label: parameterName,
                            initValue: [0, 0],
                          };
                          break;
                        case ParameterType.Coupled:
                          newParameter = {
                            type: ParameterType.Coupled,
                            dataLayerId: '',
                            parameterName: '',
                          };
                          break;
                        default:
                          return;
                      }

                      field.onChange({
                        ...parameters,
                        [parameterName]: newParameter,
                      });
                    };

                    const removeParameter = (parameterName: string) => {
                      if (
                        confirm(
                          `Are you sure you want to remove the parameter "${parameterName}"?\n\nAll configuration data will be lost instantly and cannot be recovered.`
                        )
                      ) {
                        const newParameters = { ...parameters };
                        delete newParameters[parameterName];
                        field.onChange(newParameters);
                      }
                    };

                    const updateParameter = (
                      parameterName: string,
                      updates: Partial<DataLayerParameter>
                    ) => {
                      field.onChange({
                        ...parameters,
                        [parameterName]: {
                          ...parameters[parameterName],
                          ...updates,
                        },
                      });
                    };

                    return (
                      <FormItem>
                        <div className="space-y-4">
                          {parameterKeys.length === 0 && (
                            <p className="text-gray-500 text-sm">
                              No parameters configured
                            </p>
                          )}

                          {parameterKeys.map((parameterName) => {
                            const parameter = parameters[parameterName];

                            return (
                              <div
                                key={parameterName}
                                className="p-4 border rounded-md bg-gray-50"
                              >
                                <div className="flex justify-between items-center mb-3">
                                  <h5 className="font-medium text-sm">
                                    {parameterName}
                                  </h5>
                                  <div className="flex items-center gap-2">
                                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                                      {parameter.type}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeParameter(parameterName)
                                      }
                                      disabled={saving}
                                      className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>

                                {parameter.type === ParameterType.Slider && (
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="text-xs font-medium">
                                        Label
                                      </label>
                                      <input
                                        type="text"
                                        value={
                                          (parameter as ParameterSlider).label
                                        }
                                        onChange={(e) =>
                                          updateParameter(parameterName, {
                                            label: e.target.value,
                                          })
                                        }
                                        className="w-full px-2 py-1 text-sm border rounded mt-1"
                                        disabled={saving}
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium">
                                        Min Value
                                      </label>
                                      <input
                                        type="number"
                                        value={
                                          (parameter as ParameterSlider).min
                                        }
                                        onChange={(e) =>
                                          updateParameter(parameterName, {
                                            min: Number(e.target.value),
                                          })
                                        }
                                        className="w-full px-2 py-1 text-sm border rounded mt-1"
                                        disabled={saving}
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium">
                                        Max Value
                                      </label>
                                      <input
                                        type="number"
                                        value={
                                          (parameter as ParameterSlider).max
                                        }
                                        onChange={(e) =>
                                          updateParameter(parameterName, {
                                            max: Number(e.target.value),
                                          })
                                        }
                                        className="w-full px-2 py-1 text-sm border rounded mt-1"
                                        disabled={saving}
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium">
                                        Step
                                      </label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={
                                          (parameter as ParameterSlider).step
                                        }
                                        onChange={(e) =>
                                          updateParameter(parameterName, {
                                            step: Number(e.target.value),
                                          })
                                        }
                                        className="w-full px-2 py-1 text-sm border rounded mt-1"
                                        disabled={saving}
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium">
                                        Initial Value
                                      </label>
                                      <input
                                        type="number"
                                        value={
                                          (parameter as ParameterSlider)
                                            .initValue
                                        }
                                        onChange={(e) =>
                                          updateParameter(parameterName, {
                                            initValue: Number(e.target.value),
                                          })
                                        }
                                        className="w-full px-2 py-1 text-sm border rounded mt-1"
                                        disabled={saving}
                                      />
                                    </div>
                                  </div>
                                )}

                                {parameter.type === ParameterType.Select && (
                                  <div className="space-y-3">
                                    <div>
                                      <label className="text-xs font-medium">
                                        Label
                                      </label>
                                      <input
                                        type="text"
                                        value={
                                          (parameter as ParameterSelect).label
                                        }
                                        onChange={(e) =>
                                          updateParameter(parameterName, {
                                            label: e.target.value,
                                          })
                                        }
                                        className="w-full px-2 py-1 text-sm border rounded mt-1"
                                        disabled={saving}
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium">
                                        Display As
                                      </label>
                                      <select
                                        value={
                                          (parameter as ParameterSelect)
                                            .displayAs
                                        }
                                        onChange={(e) =>
                                          updateParameter(parameterName, {
                                            displayAs: e.target
                                              .value as ParameterSelectDisplayType,
                                          })
                                        }
                                        className="w-full px-2 py-1 text-sm border rounded mt-1"
                                        disabled={saving}
                                      >
                                        <option
                                          value={
                                            ParameterSelectDisplayType.Choice
                                          }
                                        >
                                          Choice
                                        </option>
                                        <option
                                          value={
                                            ParameterSelectDisplayType.Dropdown
                                          }
                                        >
                                          Dropdown
                                        </option>
                                        <option
                                          value={
                                            ParameterSelectDisplayType.Slider
                                          }
                                        >
                                          Slider
                                        </option>
                                      </select>
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium">
                                        Options
                                      </label>
                                      <div className="space-y-2 mt-1">
                                        {(
                                          parameter as ParameterSelect
                                        ).options.map((option, optionIndex) => (
                                          <div
                                            key={optionIndex}
                                            className="flex gap-2"
                                          >
                                            <input
                                              type="text"
                                              placeholder="Value"
                                              value={option.value.toString()}
                                              onChange={(e) => {
                                                const newOptions = [
                                                  ...(
                                                    parameter as ParameterSelect
                                                  ).options,
                                                ];
                                                newOptions[optionIndex] = {
                                                  ...newOptions[optionIndex],
                                                  value: e.target.value,
                                                };
                                                const currentInitValue = (
                                                  parameter as ParameterSelect
                                                ).initValue;
                                                const isInitValueValid =
                                                  newOptions.some(
                                                    (opt) =>
                                                      opt.value.toString() ===
                                                      currentInitValue.toString()
                                                  );
                                                updateParameter(parameterName, {
                                                  options: newOptions,
                                                  ...(!isInitValueValid &&
                                                  newOptions.length > 0
                                                    ? {
                                                        initValue:
                                                          newOptions[0].value,
                                                      }
                                                    : {}),
                                                });
                                              }}
                                              className="flex-1 px-2 py-1 text-sm border rounded"
                                              disabled={saving}
                                            />
                                            <input
                                              type="text"
                                              placeholder="Label"
                                              value={option.label}
                                              onChange={(e) => {
                                                const newOptions = [
                                                  ...(
                                                    parameter as ParameterSelect
                                                  ).options,
                                                ];
                                                newOptions[optionIndex] = {
                                                  ...newOptions[optionIndex],
                                                  label: e.target.value,
                                                };
                                                updateParameter(parameterName, {
                                                  options: newOptions,
                                                });
                                              }}
                                              className="flex-1 px-2 py-1 text-sm border rounded"
                                              disabled={saving}
                                            />
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const newOptions = (
                                                  parameter as ParameterSelect
                                                ).options.filter(
                                                  (_, i) => i !== optionIndex
                                                );
                                                const currentInitValue = (
                                                  parameter as ParameterSelect
                                                ).initValue;
                                                const isInitValueValid =
                                                  newOptions.some(
                                                    (opt) =>
                                                      opt.value.toString() ===
                                                      currentInitValue.toString()
                                                  );
                                                updateParameter(parameterName, {
                                                  options: newOptions,
                                                  ...(!isInitValueValid &&
                                                  newOptions.length > 0
                                                    ? {
                                                        initValue:
                                                          newOptions[0].value,
                                                      }
                                                    : {}),
                                                });
                                              }}
                                              disabled={
                                                saving ||
                                                (parameter as ParameterSelect)
                                                  .options.length <= 1
                                              }
                                              className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                                            >
                                              ×
                                            </button>
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newOptions = [
                                              ...(parameter as ParameterSelect)
                                                .options,
                                              { value: '', label: '' },
                                            ];
                                            updateParameter(parameterName, {
                                              options: newOptions,
                                            });
                                          }}
                                          disabled={saving}
                                          className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                                        >
                                          + Add Option
                                        </button>
                                      </div>
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium">
                                        Initial Value (
                                        {
                                          (parameter as ParameterSelect)
                                            .initValue
                                        }
                                        )
                                      </label>
                                      <select
                                        value={(
                                          parameter as ParameterSelect
                                        ).initValue.toString()}
                                        onChange={(e) =>
                                          updateParameter(parameterName, {
                                            initValue: e.target.value,
                                          })
                                        }
                                        className="w-full px-2 py-1 text-sm border rounded mt-1"
                                        disabled={saving}
                                      >
                                        {(
                                          parameter as ParameterSelect
                                        ).options.map((option, i) => (
                                          <option
                                            key={i}
                                            value={option.value.toString()}
                                          >
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                )}

                                {parameter.type ===
                                  ParameterType.Coordinate && (
                                  <div className="space-y-3">
                                    <div>
                                      <label className="text-xs font-medium">
                                        Label
                                      </label>
                                      <input
                                        type="text"
                                        value={
                                          (parameter as ParameterCoordinate)
                                            .label
                                        }
                                        onChange={(e) =>
                                          updateParameter(parameterName, {
                                            label: e.target.value,
                                          })
                                        }
                                        className="w-full px-2 py-1 text-sm border rounded mt-1"
                                        disabled={saving}
                                      />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <label className="text-xs font-medium">
                                          Latitude
                                        </label>
                                        <input
                                          type="number"
                                          step="0.000001"
                                          value={
                                            (parameter as ParameterCoordinate)
                                              .initValue[0]
                                          }
                                          onChange={(e) => {
                                            const newValue: [number, number] = [
                                              Number(e.target.value),
                                              (parameter as ParameterCoordinate)
                                                .initValue[1],
                                            ];
                                            updateParameter(parameterName, {
                                              initValue: newValue,
                                            });
                                          }}
                                          className="w-full px-2 py-1 text-sm border rounded mt-1"
                                          disabled={saving}
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs font-medium">
                                          Longitude
                                        </label>
                                        <input
                                          type="number"
                                          step="0.000001"
                                          value={
                                            (parameter as ParameterCoordinate)
                                              .initValue[1]
                                          }
                                          onChange={(e) => {
                                            const newValue: [number, number] = [
                                              (parameter as ParameterCoordinate)
                                                .initValue[0],
                                              Number(e.target.value),
                                            ];
                                            updateParameter(parameterName, {
                                              initValue: newValue,
                                            });
                                          }}
                                          className="w-full px-2 py-1 text-sm border rounded mt-1"
                                          disabled={saving}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {parameter.type === ParameterType.Coupled && (
                                  <div className="space-y-3">
                                    <div>
                                      <label className="text-xs font-medium">
                                        Data Layer ID
                                      </label>
                                      <input
                                        type="text"
                                        value={
                                          (parameter as ParameterCoupled)
                                            .dataLayerId
                                        }
                                        onChange={(e) =>
                                          updateParameter(parameterName, {
                                            dataLayerId: e.target.value,
                                          })
                                        }
                                        className="w-full px-2 py-1 text-sm border rounded mt-1"
                                        disabled={saving}
                                        placeholder="Enter data layer ID"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium">
                                        Parameter Name
                                      </label>
                                      <input
                                        type="text"
                                        value={
                                          (parameter as ParameterCoupled)
                                            .parameterName
                                        }
                                        onChange={(e) =>
                                          updateParameter(parameterName, {
                                            parameterName: e.target.value,
                                          })
                                        }
                                        className="w-full px-2 py-1 text-sm border rounded mt-1"
                                        disabled={saving}
                                        placeholder="Enter parameter name to couple with"
                                      />
                                    </div>
                                  </div>
                                )}

                                {parameter.type ===
                                  ParameterType.DependentChoice && (
                                  <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <label className="text-xs font-medium">
                                          Label
                                        </label>
                                        <input
                                          type="text"
                                          value={
                                            (
                                              parameter as ParameterDependentChoice
                                            ).label
                                          }
                                          onChange={(e) =>
                                            updateParameter(parameterName, {
                                              label: e.target.value,
                                            })
                                          }
                                          className="w-full px-2 py-1 text-sm border rounded mt-1"
                                          disabled={saving}
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs font-medium">
                                          Sub Label
                                        </label>
                                        <input
                                          type="text"
                                          value={
                                            (
                                              parameter as ParameterDependentChoice
                                            ).subLabel
                                          }
                                          onChange={(e) =>
                                            updateParameter(parameterName, {
                                              subLabel: e.target.value,
                                            })
                                          }
                                          className="w-full px-2 py-1 text-sm border rounded mt-1"
                                          disabled={saving}
                                        />
                                      </div>
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium">
                                        Options
                                      </label>
                                      <div className="space-y-3 mt-1">
                                        {(
                                          parameter as ParameterDependentChoice
                                        ).options.map((option, optionIndex) => (
                                          <div
                                            key={optionIndex}
                                            className="p-3 border rounded bg-white"
                                          >
                                            <div className="flex gap-2 items-center mb-2">
                                              <input
                                                type="text"
                                                placeholder="Main Value"
                                                value={option.value.toString()}
                                                onChange={(e) => {
                                                  const newOptions = [
                                                    ...(
                                                      parameter as ParameterDependentChoice
                                                    ).options,
                                                  ];
                                                  newOptions[optionIndex] = {
                                                    ...newOptions[optionIndex],
                                                    value: e.target.value,
                                                  };
                                                  updateParameter(
                                                    parameterName,
                                                    { options: newOptions }
                                                  );
                                                }}
                                                className="flex-1 px-2 py-1 text-sm border rounded"
                                                disabled={saving}
                                              />
                                              <input
                                                type="text"
                                                placeholder="Main Label"
                                                value={option.label}
                                                onChange={(e) => {
                                                  const newOptions = [
                                                    ...(
                                                      parameter as ParameterDependentChoice
                                                    ).options,
                                                  ];
                                                  newOptions[optionIndex] = {
                                                    ...newOptions[optionIndex],
                                                    label: e.target.value,
                                                  };
                                                  updateParameter(
                                                    parameterName,
                                                    { options: newOptions }
                                                  );
                                                }}
                                                className="flex-1 px-2 py-1 text-sm border rounded"
                                                disabled={saving}
                                              />
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const newOptions = (
                                                    parameter as ParameterDependentChoice
                                                  ).options.filter(
                                                    (_, i) => i !== optionIndex
                                                  );
                                                  updateParameter(
                                                    parameterName,
                                                    { options: newOptions }
                                                  );
                                                }}
                                                disabled={
                                                  saving ||
                                                  (
                                                    parameter as ParameterDependentChoice
                                                  ).options.length <= 1
                                                }
                                                className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                                              >
                                                ×
                                              </button>
                                            </div>
                                            <div className="ml-4">
                                              <label className="text-xs font-medium text-gray-600">
                                                Sub Options:
                                              </label>
                                              <div className="space-y-1 mt-1">
                                                {option.subOptions.map(
                                                  (subOption, subIndex) => (
                                                    <div
                                                      key={subIndex}
                                                      className="flex gap-2"
                                                    >
                                                      <input
                                                        type="text"
                                                        placeholder="Sub Value"
                                                        value={subOption.value.toString()}
                                                        onChange={(e) => {
                                                          const newOptions = [
                                                            ...(
                                                              parameter as ParameterDependentChoice
                                                            ).options,
                                                          ];
                                                          newOptions[
                                                            optionIndex
                                                          ].subOptions[
                                                            subIndex
                                                          ] = {
                                                            ...newOptions[
                                                              optionIndex
                                                            ].subOptions[
                                                              subIndex
                                                            ],
                                                            value:
                                                              e.target.value,
                                                          };
                                                          updateParameter(
                                                            parameterName,
                                                            {
                                                              options:
                                                                newOptions,
                                                            }
                                                          );
                                                        }}
                                                        className="flex-1 px-2 py-1 text-xs border rounded"
                                                        disabled={saving}
                                                      />
                                                      <input
                                                        type="text"
                                                        placeholder="Sub Label"
                                                        value={subOption.label}
                                                        onChange={(e) => {
                                                          const newOptions = [
                                                            ...(
                                                              parameter as ParameterDependentChoice
                                                            ).options,
                                                          ];
                                                          newOptions[
                                                            optionIndex
                                                          ].subOptions[
                                                            subIndex
                                                          ] = {
                                                            ...newOptions[
                                                              optionIndex
                                                            ].subOptions[
                                                              subIndex
                                                            ],
                                                            label:
                                                              e.target.value,
                                                          };
                                                          updateParameter(
                                                            parameterName,
                                                            {
                                                              options:
                                                                newOptions,
                                                            }
                                                          );
                                                        }}
                                                        className="flex-1 px-2 py-1 text-xs border rounded"
                                                        disabled={saving}
                                                      />
                                                      <button
                                                        type="button"
                                                        onClick={() => {
                                                          const newOptions = [
                                                            ...(
                                                              parameter as ParameterDependentChoice
                                                            ).options,
                                                          ];
                                                          newOptions[
                                                            optionIndex
                                                          ].subOptions =
                                                            newOptions[
                                                              optionIndex
                                                            ].subOptions.filter(
                                                              (_, i) =>
                                                                i !== subIndex
                                                            );
                                                          updateParameter(
                                                            parameterName,
                                                            {
                                                              options:
                                                                newOptions,
                                                            }
                                                          );
                                                        }}
                                                        disabled={
                                                          saving ||
                                                          option.subOptions
                                                            .length <= 1
                                                        }
                                                        className="px-1 py-1 text-xs bg-red-400 text-white rounded hover:bg-red-500 disabled:opacity-50"
                                                      >
                                                        ×
                                                      </button>
                                                    </div>
                                                  )
                                                )}
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const newOptions = [
                                                      ...(
                                                        parameter as ParameterDependentChoice
                                                      ).options,
                                                    ];
                                                    newOptions[
                                                      optionIndex
                                                    ].subOptions.push({
                                                      value: '',
                                                      label: '',
                                                    });
                                                    updateParameter(
                                                      parameterName,
                                                      { options: newOptions }
                                                    );
                                                  }}
                                                  disabled={saving}
                                                  className="px-2 py-1 text-xs bg-green-400 text-white rounded hover:bg-green-500 disabled:opacity-50"
                                                >
                                                  + Add Sub Option
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newOptions = [
                                              ...(
                                                parameter as ParameterDependentChoice
                                              ).options,
                                              {
                                                value: '',
                                                label: '',
                                                subOptions: [
                                                  { value: '', label: '' },
                                                ],
                                              },
                                            ];
                                            updateParameter(parameterName, {
                                              options: newOptions,
                                            });
                                          }}
                                          disabled={saving}
                                          className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                                        >
                                          + Add Main Option
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Add Parameter Section */}
                          <div className="p-4 border-2 border-dashed border-gray-300 rounded-md">
                            <h5 className="font-medium text-sm mb-3">
                              Add New Parameter
                            </h5>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                placeholder="Parameter name"
                                className="flex-1 px-3 py-2 text-sm border rounded"
                                disabled={saving}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const input = e.target as HTMLInputElement;
                                    const select =
                                      input.nextElementSibling as HTMLSelectElement;
                                    if (input.value.trim() && select.value) {
                                      addParameter(
                                        input.value.trim(),
                                        select.value as ParameterType
                                      );
                                      input.value = '';
                                    }
                                  }
                                }}
                              />
                              <select
                                className="px-3 py-2 text-sm border rounded"
                                disabled={saving}
                                defaultValue=""
                              >
                                <option value="" disabled>
                                  Select type
                                </option>
                                <option value={ParameterType.Slider}>
                                  Slider
                                </option>
                                <option value={ParameterType.Select}>
                                  Select
                                </option>
                                <option value={ParameterType.DependentChoice}>
                                  Dependent Choice
                                </option>
                                <option value={ParameterType.Coordinate}>
                                  Coordinate
                                </option>
                                <option value={ParameterType.Coupled}>
                                  Coupled
                                </option>
                              </select>
                              <button
                                type="button"
                                onClick={() => {
                                  const container = document.querySelector(
                                    '.p-4.border-2.border-dashed'
                                  );
                                  const input = container?.querySelector(
                                    'input[type="text"]'
                                  ) as HTMLInputElement;
                                  const select = container?.querySelector(
                                    'select'
                                  ) as HTMLSelectElement;
                                  if (input?.value.trim() && select?.value) {
                                    addParameter(
                                      input.value.trim(),
                                      select.value as ParameterType
                                    );
                                    input.value = '';
                                    select.value = '';
                                  }
                                }}
                                disabled={saving}
                                className="px-4 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                              >
                                Add Parameter
                              </button>
                            </div>
                          </div>
                        </div>
                        <FormDescription>
                          Configure parameters for this data layer. Parameters
                          allow dynamic interaction with the layer's data.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>

              {/* Data Sources Editor */}
              <div className="mt-4">
                <h4 className="font-medium text-sm mb-3">Data Sources</h4>

                <FormField
                  control={form.control}
                  name="dataSources"
                  render={({ field }) => {
                    const dataSources =
                      (field.value as DataLayerData['dataSources']) || [];

                    const addDataSource = (sourceType: SourceType) => {
                      let newSource: Source;

                      switch (sourceType) {
                        case SourceType.GeoJson:
                          newSource = {
                            type: SourceType.GeoJson,
                            url: '',
                            bounds: [
                              [-90, -180],
                              [90, 180],
                            ],
                            parameterForPropertySelectorColoring: null,
                            propertyForSelectorColoring: null,
                          };
                          break;
                        case SourceType.TilesTMS:
                          newSource = {
                            type: SourceType.TilesTMS,
                            url: '',
                            minZoom: 0,
                            maxZoom: 18,
                            minValue: 0,
                            maxValue: 100,
                            bounds: [
                              [-90, -180],
                              [90, 180],
                            ],
                          };
                          break;
                        case SourceType.GeoRaster:
                          newSource = {
                            type: SourceType.GeoRaster,
                            url: '',
                          };
                          break;
                        case SourceType.None:
                        default:
                          newSource = { type: SourceType.None };
                          break;
                      }

                      const newDataSource = {
                        dataSourceTriggers: [],
                        dataSource: newSource,
                        coloring: { type: ColoringType.None },
                      };
                      field.onChange([...dataSources, newDataSource]);
                    };

                    const removeDataSource = (index: number) => {
                      if (
                        confirm(
                          'Are you sure you want to remove this data source?\n\nAll configuration data will be lost instantly and cannot be recovered.'
                        )
                      ) {
                        const newDataSources = dataSources.filter(
                          (_, i) => i !== index
                        );
                        field.onChange(newDataSources);
                      }
                    };

                    const updateDataSource = (
                      index: number,
                      updates: Partial<(typeof dataSources)[0]>
                    ) => {
                      const newDataSources = [...dataSources];
                      newDataSources[index] = {
                        ...newDataSources[index],
                        ...updates,
                      };
                      field.onChange(newDataSources);
                    };

                    const updateSource = (index: number, newSource: Source) => {
                      updateDataSource(index, { dataSource: newSource });
                    };

                    return (
                      <FormItem>
                        <div className="space-y-4">
                          {dataSources.map((dataSource, index) => (
                            <div
                              key={index}
                              className="p-4 border rounded bg-gray-50"
                            >
                              <div className="flex justify-between items-center mb-3">
                                <h5 className="font-medium">
                                  Data Source {index + 1}
                                </h5>
                                <button
                                  type="button"
                                  onClick={() => removeDataSource(index)}
                                  disabled={saving}
                                  className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                                >
                                  Remove
                                </button>
                              </div>

                              {/* Source Type Display (non-editable) */}
                              <div className="mb-3">
                                <label className="text-sm font-medium mb-2 block">
                                  Source Type
                                </label>
                                <input
                                  type="text"
                                  value={
                                    dataSource.dataSource.type ===
                                    SourceType.GeoJson
                                      ? 'GeoJSON'
                                      : dataSource.dataSource.type ===
                                          SourceType.TilesTMS
                                        ? 'Tiles TMS'
                                        : dataSource.dataSource.type ===
                                            SourceType.GeoRaster
                                          ? 'GeoRaster'
                                          : 'None'
                                  }
                                  disabled
                                  className="w-full px-3 py-2 border rounded bg-gray-100 text-gray-600"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  Source type is set when creating the data
                                  source and cannot be changed.
                                </p>
                              </div>

                              {/* Source-specific fields */}
                              {dataSource.dataSource.type ===
                                SourceType.GeoJson && (
                                <div className="space-y-3">
                                  <div>
                                    <label className="text-sm font-medium mb-1 block">
                                      GeoJSON URL
                                    </label>
                                    <input
                                      type="text"
                                      value={
                                        (dataSource.dataSource as SourceGeoJson)
                                          .url
                                      }
                                      onChange={(e) =>
                                        updateSource(index, {
                                          ...dataSource.dataSource,
                                          url: e.target.value,
                                        } as SourceGeoJson)
                                      }
                                      placeholder="/static/projects/my-project/data.geojson"
                                      disabled={saving}
                                      className="w-full px-3 py-2 border rounded"
                                    />
                                    <p className="text-xs text-gray-600 mt-1">
                                      Use parameter placeholders like{' '}
                                      <code className="bg-gray-100 px-1 rounded">{`{:parameter_name:}`}</code>{' '}
                                      in the URL. Example:{' '}
                                      <code className="bg-gray-100 px-1 rounded">
                                        /static/projects/&lt;project_id&gt;/&lt;data_layer_id&gt;/
                                        {`{:year:}`}_{`{:month:}`}.geojson
                                      </code>
                                    </p>
                                  </div>
                                  <div>
                                    <div className="flex justify-between items-center mb-2">
                                      <label className="text-sm font-medium">
                                        Geographic Bounds
                                      </label>
                                      <div className={'flex'}>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            updateSource(index, {
                                              ...dataSource.dataSource,
                                              bounds: [
                                                [51.2334, 11.1231],
                                                [53.6015, 14.8713],
                                              ],
                                            } as SourceGeoJson);
                                          }}
                                          disabled={saving}
                                          className="cursor-pointer px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                                        >
                                          Set Brandenburg
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            updateSource(index, {
                                              ...dataSource.dataSource,
                                              bounds: [
                                                [52.327157, 13.066864],
                                                [52.684707, 13.781318],
                                              ],
                                            } as SourceGeoJson);
                                          }}
                                          disabled={saving}
                                          className="cursor-pointer px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                                        >
                                          Set Berlin
                                        </button>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <label className="text-xs text-gray-600 mb-1 block">
                                          Southwest Latitude
                                        </label>
                                        <input
                                          type="number"
                                          step="any"
                                          value={
                                            (
                                              dataSource.dataSource as SourceGeoJson
                                            ).bounds[0][0]
                                          }
                                          onChange={(e) => {
                                            const bounds = [
                                              ...(
                                                dataSource.dataSource as SourceGeoJson
                                              ).bounds,
                                            ] as [
                                              [number, number],
                                              [number, number],
                                            ];
                                            bounds[0] = [
                                              parseFloat(e.target.value) || 0,
                                              bounds[0][1],
                                            ];
                                            updateSource(index, {
                                              ...dataSource.dataSource,
                                              bounds,
                                            } as SourceGeoJson);
                                          }}
                                          placeholder="-90"
                                          disabled={saving}
                                          className="w-full px-3 py-2 border rounded"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-600 mb-1 block">
                                          Southwest Longitude
                                        </label>
                                        <input
                                          type="number"
                                          step="any"
                                          value={
                                            (
                                              dataSource.dataSource as SourceGeoJson
                                            ).bounds[0][1]
                                          }
                                          onChange={(e) => {
                                            const bounds = [
                                              ...(
                                                dataSource.dataSource as SourceGeoJson
                                              ).bounds,
                                            ] as [
                                              [number, number],
                                              [number, number],
                                            ];
                                            bounds[0] = [
                                              bounds[0][0],
                                              parseFloat(e.target.value) || 0,
                                            ];
                                            updateSource(index, {
                                              ...dataSource.dataSource,
                                              bounds,
                                            } as SourceGeoJson);
                                          }}
                                          placeholder="-180"
                                          disabled={saving}
                                          className="w-full px-3 py-2 border rounded"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-600 mb-1 block">
                                          Northeast Latitude
                                        </label>
                                        <input
                                          type="number"
                                          step="any"
                                          value={
                                            (
                                              dataSource.dataSource as SourceGeoJson
                                            ).bounds[1][0]
                                          }
                                          onChange={(e) => {
                                            const bounds = [
                                              ...(
                                                dataSource.dataSource as SourceGeoJson
                                              ).bounds,
                                            ] as [
                                              [number, number],
                                              [number, number],
                                            ];
                                            bounds[1] = [
                                              parseFloat(e.target.value) || 0,
                                              bounds[1][1],
                                            ];
                                            updateSource(index, {
                                              ...dataSource.dataSource,
                                              bounds,
                                            } as SourceGeoJson);
                                          }}
                                          placeholder="90"
                                          disabled={saving}
                                          className="w-full px-3 py-2 border rounded"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-600 mb-1 block">
                                          Northeast Longitude
                                        </label>
                                        <input
                                          type="number"
                                          step="any"
                                          value={
                                            (
                                              dataSource.dataSource as SourceGeoJson
                                            ).bounds[1][1]
                                          }
                                          onChange={(e) => {
                                            const bounds = [
                                              ...(
                                                dataSource.dataSource as SourceGeoJson
                                              ).bounds,
                                            ] as [
                                              [number, number],
                                              [number, number],
                                            ];
                                            bounds[1] = [
                                              bounds[1][0],
                                              parseFloat(e.target.value) || 0,
                                            ];
                                            updateSource(index, {
                                              ...dataSource.dataSource,
                                              bounds,
                                            } as SourceGeoJson);
                                          }}
                                          placeholder="180"
                                          disabled={saving}
                                          className="w-full px-3 py-2 border rounded"
                                        />
                                      </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Define the geographic bounding box for
                                      this data source used for automatic
                                      zooming to the map area. Format: [[SW Lat,
                                      SW Lng], [NE Lat, NE Lng]]
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="text-sm font-medium mb-1 block">
                                        Property for Coloring
                                      </label>
                                      <input
                                        type="text"
                                        value={
                                          (
                                            dataSource.dataSource as SourceGeoJson
                                          ).propertyForSelectorColoring ?? ''
                                        }
                                        onChange={(e) =>
                                          updateSource(index, {
                                            ...dataSource.dataSource,
                                            propertyForSelectorColoring:
                                              e.target.value || null,
                                          } as SourceGeoJson)
                                        }
                                        placeholder="property_name"
                                        disabled={saving}
                                        className="w-full px-3 py-2 border rounded"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium mb-1 block">
                                        Parameter for Coloring
                                      </label>
                                      <input
                                        type="text"
                                        value={
                                          (
                                            dataSource.dataSource as SourceGeoJson
                                          )
                                            .parameterForPropertySelectorColoring ??
                                          ''
                                        }
                                        onChange={(e) =>
                                          updateSource(index, {
                                            ...dataSource.dataSource,
                                            parameterForPropertySelectorColoring:
                                              e.target.value || null,
                                          } as SourceGeoJson)
                                        }
                                        placeholder="parameter_name"
                                        disabled={saving}
                                        className="w-full px-3 py-2 border rounded"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}

                              {dataSource.dataSource.type ===
                                SourceType.TilesTMS && (
                                <div className="space-y-3">
                                  <div>
                                    <label className="text-sm font-medium mb-1 block">
                                      Tiles URL
                                    </label>
                                    <input
                                      type="text"
                                      value={
                                        (
                                          dataSource.dataSource as SourceTilesTMS
                                        ).url
                                      }
                                      onChange={(e) =>
                                        updateSource(index, {
                                          ...dataSource.dataSource,
                                          url: e.target.value,
                                        } as SourceTilesTMS)
                                      }
                                      placeholder="/tiles/{z}/{x}/{y}.png"
                                      disabled={saving}
                                      className="w-full px-3 py-2 border rounded"
                                    />
                                    <p className="text-xs text-gray-600 mt-1">
                                      Use parameter placeholders like{' '}
                                      <code className="bg-gray-100 px-1 rounded">{`{:parameter_name:}`}</code>{' '}
                                      in the URL. Example:{' '}
                                      <code className="bg-gray-100 px-1 rounded">
                                        /tiles/{`{:year:}`}/{`{z}`}/{`{x}`}/
                                        {`{y}`}.png
                                      </code>
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="text-sm font-medium mb-1 block">
                                        Min Zoom
                                      </label>
                                      <input
                                        type="number"
                                        value={
                                          (
                                            dataSource.dataSource as SourceTilesTMS
                                          ).minZoom
                                        }
                                        onChange={(e) =>
                                          updateSource(index, {
                                            ...dataSource.dataSource,
                                            minZoom:
                                              parseInt(e.target.value) || 0,
                                          } as SourceTilesTMS)
                                        }
                                        disabled={saving}
                                        className="w-full px-3 py-2 border rounded"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium mb-1 block">
                                        Max Zoom
                                      </label>
                                      <input
                                        type="number"
                                        value={
                                          (
                                            dataSource.dataSource as SourceTilesTMS
                                          ).maxZoom
                                        }
                                        onChange={(e) =>
                                          updateSource(index, {
                                            ...dataSource.dataSource,
                                            maxZoom:
                                              parseInt(e.target.value) || 18,
                                          } as SourceTilesTMS)
                                        }
                                        disabled={saving}
                                        className="w-full px-3 py-2 border rounded"
                                      />
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="text-sm font-medium mb-1 block">
                                        Min Value
                                      </label>
                                      <input
                                        type="number"
                                        value={
                                          (
                                            dataSource.dataSource as SourceTilesTMS
                                          ).minValue
                                        }
                                        onChange={(e) =>
                                          updateSource(index, {
                                            ...dataSource.dataSource,
                                            minValue:
                                              parseFloat(e.target.value) || 0,
                                          } as SourceTilesTMS)
                                        }
                                        disabled={saving}
                                        step="any"
                                        className="w-full px-3 py-2 border rounded"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium mb-1 block">
                                        Max Value
                                      </label>
                                      <input
                                        type="number"
                                        value={
                                          (
                                            dataSource.dataSource as SourceTilesTMS
                                          ).maxValue
                                        }
                                        onChange={(e) =>
                                          updateSource(index, {
                                            ...dataSource.dataSource,
                                            maxValue:
                                              parseFloat(e.target.value) || 100,
                                          } as SourceTilesTMS)
                                        }
                                        disabled={saving}
                                        step="any"
                                        className="w-full px-3 py-2 border rounded"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <div className="flex justify-between items-center mb-2">
                                      <label className="text-sm font-medium">
                                        Geographic Bounds
                                      </label>
                                      <div className={'flex'}>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            updateSource(index, {
                                              ...dataSource.dataSource,
                                              bounds: [
                                                [51.2334, 11.1231],
                                                [53.6015, 14.8713],
                                              ],
                                            } as SourceTilesTMS);
                                          }}
                                          disabled={saving}
                                          className="cursor-pointer px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                                        >
                                          Set Brandenburg
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            updateSource(index, {
                                              ...dataSource.dataSource,
                                              bounds: [
                                                [52.327157, 13.066864],
                                                [52.684707, 13.781318],
                                              ],
                                            } as SourceTilesTMS);
                                          }}
                                          disabled={saving}
                                          className="cursor-pointer px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                                        >
                                          Set Berlin
                                        </button>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <label className="text-xs text-gray-600 mb-1 block">
                                          Southwest Latitude
                                        </label>
                                        <input
                                          type="number"
                                          step="any"
                                          value={
                                            (
                                              dataSource.dataSource as SourceTilesTMS
                                            ).bounds[0][0]
                                          }
                                          onChange={(e) => {
                                            const bounds = [
                                              ...(
                                                dataSource.dataSource as SourceTilesTMS
                                              ).bounds,
                                            ] as [
                                              [number, number],
                                              [number, number],
                                            ];
                                            bounds[0] = [
                                              parseFloat(e.target.value) || 0,
                                              bounds[0][1],
                                            ];
                                            updateSource(index, {
                                              ...dataSource.dataSource,
                                              bounds,
                                            } as SourceTilesTMS);
                                          }}
                                          placeholder="-90"
                                          disabled={saving}
                                          className="w-full px-3 py-2 border rounded"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-600 mb-1 block">
                                          Southwest Longitude
                                        </label>
                                        <input
                                          type="number"
                                          step="any"
                                          value={
                                            (
                                              dataSource.dataSource as SourceTilesTMS
                                            ).bounds[0][1]
                                          }
                                          onChange={(e) => {
                                            const bounds = [
                                              ...(
                                                dataSource.dataSource as SourceTilesTMS
                                              ).bounds,
                                            ] as [
                                              [number, number],
                                              [number, number],
                                            ];
                                            bounds[0] = [
                                              bounds[0][0],
                                              parseFloat(e.target.value) || 0,
                                            ];
                                            updateSource(index, {
                                              ...dataSource.dataSource,
                                              bounds,
                                            } as SourceTilesTMS);
                                          }}
                                          placeholder="-180"
                                          disabled={saving}
                                          className="w-full px-3 py-2 border rounded"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-600 mb-1 block">
                                          Northeast Latitude
                                        </label>
                                        <input
                                          type="number"
                                          step="any"
                                          value={
                                            (
                                              dataSource.dataSource as SourceTilesTMS
                                            ).bounds[1][0]
                                          }
                                          onChange={(e) => {
                                            const bounds = [
                                              ...(
                                                dataSource.dataSource as SourceTilesTMS
                                              ).bounds,
                                            ] as [
                                              [number, number],
                                              [number, number],
                                            ];
                                            bounds[1] = [
                                              parseFloat(e.target.value) || 0,
                                              bounds[1][1],
                                            ];
                                            updateSource(index, {
                                              ...dataSource.dataSource,
                                              bounds,
                                            } as SourceTilesTMS);
                                          }}
                                          placeholder="90"
                                          disabled={saving}
                                          className="w-full px-3 py-2 border rounded"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-600 mb-1 block">
                                          Northeast Longitude
                                        </label>
                                        <input
                                          type="number"
                                          step="any"
                                          value={
                                            (
                                              dataSource.dataSource as SourceTilesTMS
                                            ).bounds[1][1]
                                          }
                                          onChange={(e) => {
                                            const bounds = [
                                              ...(
                                                dataSource.dataSource as SourceTilesTMS
                                              ).bounds,
                                            ] as [
                                              [number, number],
                                              [number, number],
                                            ];
                                            bounds[1] = [
                                              bounds[1][0],
                                              parseFloat(e.target.value) || 0,
                                            ];
                                            updateSource(index, {
                                              ...dataSource.dataSource,
                                              bounds,
                                            } as SourceTilesTMS);
                                          }}
                                          placeholder="180"
                                          disabled={saving}
                                          className="w-full px-3 py-2 border rounded"
                                        />
                                      </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Define the geographic bounding box for
                                      this data source. Format: [[SW Lat, SW
                                      Lng], [NE Lat, NE Lng]]
                                    </p>
                                  </div>
                                </div>
                              )}

                              {dataSource.dataSource.type ===
                                SourceType.GeoRaster && (
                                <div>
                                  <label className="text-sm font-medium mb-1 block">
                                    GeoRaster URL
                                  </label>
                                  <input
                                    type="text"
                                    value={
                                      (dataSource.dataSource as SourceGeoRaster)
                                        .url
                                    }
                                    onChange={(e) =>
                                      updateSource(index, {
                                        ...dataSource.dataSource,
                                        url: e.target.value,
                                      } as SourceGeoRaster)
                                    }
                                    placeholder="/static/raster/data.tif"
                                    disabled={saving}
                                    className="w-full px-3 py-2 border rounded"
                                  />
                                  <p className="text-xs text-gray-600 mt-1">
                                    Use parameter placeholders like{' '}
                                    <code className="bg-gray-100 px-1 rounded">{`{:parameter_name:}`}</code>{' '}
                                    in the URL. Example:{' '}
                                    <code className="bg-gray-100 px-1 rounded">
                                      /raster/{`{:scenario:}`}_{`{:year:}`}.tif
                                    </code>
                                  </p>
                                </div>
                              )}

                              {/* Data Source Triggers */}
                              <div className="mt-3">
                                <label className="text-sm font-medium mb-2 block">
                                  Triggers
                                </label>
                                <div className="space-y-2">
                                  <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-2 border rounded bg-gray-50">
                                    {dataSource.dataSourceTriggers.map(
                                      (trigger, triggerIndex) => (
                                        <span
                                          key={triggerIndex}
                                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                                        >
                                          {trigger}
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newTriggers =
                                                dataSource.dataSourceTriggers.filter(
                                                  (_, i) => i !== triggerIndex
                                                );
                                              updateDataSource(index, {
                                                dataSourceTriggers: newTriggers,
                                              });
                                            }}
                                            disabled={saving}
                                            className="ml-1 hover:bg-blue-200 rounded-full w-4 h-4 flex items-center justify-center text-xs"
                                          >
                                            ×
                                          </button>
                                        </span>
                                      )
                                    )}
                                    {dataSource.dataSourceTriggers.length ===
                                      0 && (
                                      <span className="text-gray-400 text-sm">
                                        No triggers added
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      placeholder="Add trigger"
                                      disabled={saving}
                                      className="flex-1 px-3 py-2 border rounded text-sm"
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === 'Enter' &&
                                          e.currentTarget.value.trim()
                                        ) {
                                          e.preventDefault();
                                          const newTrigger =
                                            e.currentTarget.value.trim();
                                          if (
                                            !dataSource.dataSourceTriggers.includes(
                                              newTrigger
                                            )
                                          ) {
                                            updateDataSource(index, {
                                              dataSourceTriggers: [
                                                ...dataSource.dataSourceTriggers,
                                                newTrigger,
                                              ],
                                            });
                                          }
                                          e.currentTarget.value = '';
                                        }
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        const input = e.currentTarget
                                          .previousElementSibling as HTMLInputElement;
                                        const newTrigger = input.value.trim();
                                        if (
                                          newTrigger &&
                                          !dataSource.dataSourceTriggers.includes(
                                            newTrigger
                                          )
                                        ) {
                                          updateDataSource(index, {
                                            dataSourceTriggers: [
                                              ...dataSource.dataSourceTriggers,
                                              newTrigger,
                                            ],
                                          });
                                          input.value = '';
                                        }
                                      }}
                                      disabled={saving}
                                      className="px-3 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                                    >
                                      Add
                                    </button>
                                  </div>
                                  <p className="text-xs text-gray-500">
                                    If this data layer uses parameters, press
                                    Enter or click Add to create triggers using
                                    the name of the parameter. Click × to
                                    remove.
                                  </p>
                                </div>
                              </div>

                              {/* Coloring Configuration */}
                              <div className="mt-4">
                                <label className="text-sm font-medium mb-2 block">
                                  Coloring
                                </label>

                                {/* Coloring Type Selector */}
                                <div className="mb-3">
                                  <select
                                    value={dataSource.coloring.type}
                                    onChange={(e) => {
                                      const newType = e.target
                                        .value as ColoringType;
                                      let newColoring;

                                      switch (newType) {
                                        case ColoringType.None:
                                          newColoring = {
                                            type: ColoringType.None,
                                          } as ColoringNone;
                                          break;
                                        case ColoringType.Flat:
                                          newColoring = {
                                            type: ColoringType.Flat,
                                            color: '#000000',
                                            legend: {
                                              type: FlatLegendType.Flat,
                                              label: '',
                                            },
                                          } as ColoringFlat;
                                          break;
                                        case ColoringType.Categories:
                                          newColoring = {
                                            type: ColoringType.Categories,
                                            fallback: '#cccccc',
                                            colorMap: {},
                                          } as ColorCategories;
                                          break;
                                        case ColoringType.Bands:
                                          newColoring = {
                                            type: ColoringType.Bands,
                                            bands: [
                                              { color: '#000000', legend: '' },
                                            ],
                                          } as ColoringBands;
                                          break;
                                        case ColoringType.Scale:
                                          newColoring = {
                                            type: ColoringType.Scale,
                                            min: 0,
                                            max: 100,
                                            colors: ['#0000ff', '#ff0000'],
                                            legend: {
                                              minLabel: '',
                                              maxLabel: '',
                                            },
                                          } as ColorScale;
                                          break;
                                        default:
                                          newColoring = {
                                            type: ColoringType.None,
                                          } as ColoringNone;
                                      }

                                      updateDataSource(index, {
                                        coloring: newColoring,
                                      });
                                    }}
                                    disabled={saving}
                                    className="w-full px-3 py-2 border rounded text-sm"
                                  >
                                    <option value={ColoringType.None}>
                                      None
                                    </option>
                                    <option value={ColoringType.Flat}>
                                      Flat
                                    </option>
                                    <option value={ColoringType.Categories}>
                                      Categories
                                    </option>
                                    <option value={ColoringType.Bands}>
                                      Bands
                                    </option>
                                    <option value={ColoringType.Scale}>
                                      Scale
                                    </option>
                                  </select>
                                </div>

                                {/* Coloring Type-Specific Forms */}
                                {dataSource.coloring.type ===
                                  ColoringType.None && (
                                  <ColoringNoneForm
                                    coloring={
                                      dataSource.coloring as ColoringNone
                                    }
                                    onChange={(newColoring) =>
                                      updateDataSource(index, {
                                        coloring: newColoring,
                                      })
                                    }
                                    disabled={saving}
                                  />
                                )}

                                {dataSource.coloring.type ===
                                  ColoringType.Flat && (
                                  <ColoringFlatForm
                                    coloring={
                                      dataSource.coloring as ColoringFlat
                                    }
                                    onChange={(newColoring) =>
                                      updateDataSource(index, {
                                        coloring: newColoring,
                                      })
                                    }
                                    disabled={saving}
                                  />
                                )}

                                {dataSource.coloring.type ===
                                  ColoringType.Categories && (
                                  <ColoringCategoriesForm
                                    coloring={
                                      dataSource.coloring as ColorCategories
                                    }
                                    onChange={(newColoring) =>
                                      updateDataSource(index, {
                                        coloring: newColoring,
                                      })
                                    }
                                    disabled={saving}
                                  />
                                )}

                                {dataSource.coloring.type ===
                                  ColoringType.Bands && (
                                  <ColoringBandsForm
                                    coloring={
                                      dataSource.coloring as ColoringBands
                                    }
                                    onChange={(newColoring) =>
                                      updateDataSource(index, {
                                        coloring: newColoring,
                                      })
                                    }
                                    disabled={saving}
                                  />
                                )}

                                {dataSource.coloring.type ===
                                  ColoringType.Scale && (
                                  <ColoringScaleForm
                                    coloring={dataSource.coloring as ColorScale}
                                    onChange={(newColoring) =>
                                      updateDataSource(index, {
                                        coloring: newColoring,
                                      })
                                    }
                                    disabled={saving}
                                  />
                                )}
                              </div>
                            </div>
                          ))}

                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => addDataSource(SourceType.GeoJson)}
                              disabled={saving}
                              className="px-3 py-2 text-sm bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                            >
                              + Add GeoJSON Source
                            </button>
                            <button
                              type="button"
                              onClick={() => addDataSource(SourceType.TilesTMS)}
                              disabled={saving}
                              className="px-3 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                            >
                              + Add Tiles TMS Source
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                addDataSource(SourceType.GeoRaster)
                              }
                              disabled={saving}
                              className="px-3 py-2 text-sm bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50"
                            >
                              + Add GeoRaster Source
                            </button>
                            {/*<button*/}
                            {/*  type="button"*/}
                            {/*  onClick={() => addDataSource(SourceType.None)}*/}
                            {/*  disabled={saving}*/}
                            {/*  className="px-3 py-2 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"*/}
                            {/*>*/}
                            {/*  + Add None Source*/}
                            {/*</button>*/}
                          </div>
                        </div>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
            </div>
          )}

          {/* Plain Project specific fields */}
          {watchedType === DataLayerType.PlainProject && (
            <div className="mt-6 p-4 border rounded-md bg-yellow-50">
              <h3 className="font-semibold mb-4">Markers</h3>

              <FormField
                control={form.control}
                name="markers"
                render={({ field }) => {
                  const markers = (field.value as [number, number][]) || [];

                  const addMarker = () => {
                    const newMarkers = [...markers, [0, 0] as [number, number]];
                    field.onChange(newMarkers);
                  };

                  const removeMarker = (index: number) => {
                    if (
                      confirm(
                        'Are you sure you want to remove this marker?\n\nThe marker coordinates will be lost instantly and cannot be recovered.'
                      )
                    ) {
                      const newMarkers = markers.filter((_, i) => i !== index);
                      field.onChange(newMarkers);
                    }
                  };

                  const updateMarker = (
                    index: number,
                    lat: number,
                    lng: number
                  ) => {
                    const newMarkers = [...markers];
                    newMarkers[index] = [lat, lng];
                    field.onChange(newMarkers);
                  };

                  return (
                    <FormItem>
                      <div className="space-y-3">
                        {markers.map((marker, index) => (
                          <div
                            key={index}
                            className="flex gap-2 items-center p-3 bg-white border rounded"
                          >
                            <div className="flex-1">
                              <label className="text-xs text-gray-500">
                                Latitude
                              </label>
                              <input
                                type="number"
                                value={marker[0]}
                                onChange={(e) =>
                                  updateMarker(
                                    index,
                                    parseFloat(e.target.value) || 0,
                                    marker[1]
                                  )
                                }
                                placeholder="Latitude"
                                disabled={saving}
                                step="any"
                                className="w-full px-2 py-1 text-sm border rounded mt-1"
                              />
                            </div>
                            <div className="flex-1">
                              <label className="text-xs text-gray-500">
                                Longitude
                              </label>
                              <input
                                type="number"
                                value={marker[1]}
                                onChange={(e) =>
                                  updateMarker(
                                    index,
                                    marker[0],
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                placeholder="Longitude"
                                disabled={saving}
                                step="any"
                                className="w-full px-2 py-1 text-sm border rounded mt-1"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeMarker(index)}
                              disabled={saving || markers.length <= 1}
                              className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 self-end"
                            >
                              Remove
                            </button>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={addMarker}
                          disabled={saving}
                          className="px-4 py-2 text-sm bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                        >
                          + Add Marker
                        </button>
                      </div>
                      <FormDescription>
                        Add geographic markers for this layer. Each marker
                        requires latitude and longitude coordinates.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>
          )}

          <div className={cn('py-[8px]', 'flex', 'gap-x-[12px]', 'mt-6')}>
            <button
              type="submit"
              disabled={saving}
              className="cursor-pointer inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
            >
              {saving
                ? 'Saving...'
                : isEditing
                  ? 'Update Data Layer'
                  : 'Create Data Layer'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </form>
      </Form>
    </>
  );
}
